import React, { useEffect, useState, useRef } from "react";
import "./CartPage.css";
import { useCart } from "../context/CartContext";
import {
  trackCartPhoneCaptured,
  trackCartPhoneCompleted,
  trackCartPhoneSkipped,
  trackCartStepViewed,
  trackCartTableCompleted,
  trackCartAbandoned,
} from "../utils/analytics";

const isValidIndianPhone = (value) => {
  const digits = value.replace(/\D/g, "");
  if (digits.startsWith("91") && digits.length === 12) return true;
  if (digits.length === 10 && /^[6-9]/.test(digits)) return true;
  return false;
};

const EditIcon = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M8.5 1.5l2 2M1 11l.7-2.8L9.2 .7a1 1 0 011.4 0l.7.7a1 1 0 010 1.4L3.8 10.3 1 11z" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const NEW_TAG_ID = "6932636dec4624b7afca0266";

// Dietary indicators (veg / non-veg / egg / spicy) for a cart item.
const renderDietaryTags = (item) => {
  const tags = Array.isArray(item.tags) ? item.tags : [];
  if (tags.length === 0) return null;
  const hasEgg = tags.some((t) => t?.name?.toLowerCase().includes("egg"));
  const hasNonVeg = tags.some((t) => {
    const n = t?.name?.toLowerCase() || "";
    return n.includes("non") && n.includes("veg");
  });
  const icons = [];
  if (hasEgg) {
    icons.push(<img key="egg" src="/EggTag.png" alt="Egg" className="cart-row-diet-egg" />);
  }
  tags.forEach((t, i) => {
    const name = t?.name?.toLowerCase() || "";
    if (t?.id === NEW_TAG_ID || name.includes("new")) return;
    if (hasEgg && name.includes("egg")) return;
    if (hasEgg && hasNonVeg && name.includes("non")) return;
    if (t?.tagIcon?.fullPath) {
      icons.push(<img key={t.id || i} src={t.tagIcon.fullPath} alt={t.name || ""} className="cart-row-diet-icon" />);
    }
  });
  if (icons.length === 0) return null;
  return <span className="cart-row-diet-tags">{icons}</span>;
};

export default function CartPage({ cafeId, cafeName, onBack, onEditItem, onViewSummary }) {
  const {
    items,
    itemCount,
    subtotal,
    taxTotal,
    grandTotal,
    updateQuantity,
    removeItem,
    phoneNumber,
    setPhoneNumber,
    tableNumber,
    setTableNumber,
  } = useCart();

  const [step, setStep] = useState(() => {
    if (tableNumber.trim().length > 0) return 1;
    return 0;
  });
  const [returnStep, setReturnStep] = useState(null);
  const [tableError, setTableError] = useState(false);
  const [phoneError, setPhoneError] = useState(false);
  const tableRef = useRef(null);
  const phoneRef = useRef(null);
  const viewedStepsRef = useRef(new Set());

  const cartPageRef = useRef(null);

  useEffect(() => {
    const scrollY = window.scrollY;
    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = "100%";
    document.body.style.overflow = "hidden";

    // Keyboard handling: on Android, interactive-widget=resizes-content shrinks
    // the layout viewport so the fixed panel sits above the keyboard on its own.
    // iOS Safari ignores that descriptor, so we pin the panel to the visual
    // viewport height as a fallback. We always keep top:0 (never offsetTop) to
    // avoid the panel drifting when the browser scroll-adjusts for the input.
    const vv = window.visualViewport;
    const applyViewport = () => {
      const el = cartPageRef.current;
      if (!el || !vv) return;
      el.style.height = `${vv.height}px`;
      el.style.top = "0px";
    };

    if (vv) {
      vv.addEventListener("resize", applyViewport);
      vv.addEventListener("scroll", applyViewport);
      applyViewport();
    }

    return () => {
      if (vv) {
        vv.removeEventListener("resize", applyViewport);
        vv.removeEventListener("scroll", applyViewport);
      }
      const el = cartPageRef.current;
      if (el) { el.style.height = ""; el.style.top = ""; }
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.width = "";
      document.body.style.overflow = "";
      window.scrollTo(0, scrollY);
    };
  }, []);

  useEffect(() => {
    const isTouch = "ontouchstart" in window || navigator.maxTouchPoints > 0;
    if (isTouch) return;
    if (step === 0 && tableRef.current) {
      setTimeout(() => tableRef.current?.focus(), 300);
    }
    if (step === 1 && phoneRef.current) {
      setTimeout(() => phoneRef.current?.focus(), 300);
    }
  }, [step]);

  useEffect(() => {
    const stepName = step === 0 ? "table" : step === 1 ? "phone" : "summary_ready";
    const key = `${stepName}-${itemCount}-${Boolean(tableNumber)}-${Boolean(phoneNumber)}`;
    if (viewedStepsRef.current.has(key)) return;
    viewedStepsRef.current.add(key);

    trackCartStepViewed({
      cafeId,
      cafeName,
      stepName,
      itemCount,
      lineCount: items.length,
      subtotal,
      taxTotal,
      grandTotal,
      hasTableNumber: Boolean(tableNumber),
      hasPhoneNumber: Boolean(phoneNumber),
    });
  }, [cafeId, cafeName, step, itemCount, items.length, subtotal, taxTotal, grandTotal, tableNumber, phoneNumber]);

  const goToEdit = (targetStep) => {
    setReturnStep(step);
    setStep(targetStep);
  };

  const handleTableContinue = () => {
    if (!tableNumber.trim().length) {
      setTableError(true);
      tableRef.current?.focus();
      return;
    }
    setTableError(false);
    trackCartTableCompleted({
      cafeId,
      cafeName,
      itemCount,
      lineCount: items.length,
      subtotal,
      taxTotal,
      grandTotal,
      hasTableNumber: true,
      hasPhoneNumber: Boolean(phoneNumber),
    });
    if (returnStep !== null && returnStep > 0) {
      const dest = returnStep;
      setReturnStep(null);
      setStep(dest);
    } else {
      setReturnStep(null);
      setStep(1);
    }
  };

  const handlePhoneContinue = () => {
    if (phoneNumber.length > 0 && !isValidIndianPhone(phoneNumber)) {
      setPhoneError(true);
      phoneRef.current?.focus();
      return;
    }
    setPhoneError(false);
    const hasValidPhoneNumber = phoneNumber.trim().length > 0;
    const commonParams = {
      cafeId,
      cafeName,
      itemCount,
      lineCount: items.length,
      subtotal,
      taxTotal,
      grandTotal,
      hasTableNumber: Boolean(tableNumber),
      hasPhoneNumber: hasValidPhoneNumber,
    };
    trackCartPhoneCompleted({
      ...commonParams,
    });
    if (hasValidPhoneNumber) {
      trackCartPhoneCaptured(commonParams);
    }
    setReturnStep(null);
    onViewSummary();
  };

  const handlePhoneSkip = () => {
    setPhoneNumber("");
    setPhoneError(false);
    trackCartPhoneSkipped({
      cafeId,
      cafeName,
      itemCount,
      lineCount: items.length,
      subtotal,
      taxTotal,
      grandTotal,
      hasTableNumber: Boolean(tableNumber),
      hasPhoneNumber: false,
    });
    setReturnStep(null);
    onViewSummary();
  };

  const stepNames = ["table", "phone", "summary_ready"];
  const handleBack = () => {
    trackCartAbandoned({
      cafeId,
      cafeName,
      abandonStep: stepNames[step] || "unknown",
      itemCount,
      lineCount: items.length,
      subtotal,
      taxTotal,
      grandTotal,
      hasTableNumber: Boolean(tableNumber),
      hasPhoneNumber: Boolean(phoneNumber),
    });
    onBack();
  };

  const stepLabel = (idx, label, done) => (
    <div className={`cart-step-dot ${step === idx ? "active" : ""} ${done ? "done" : ""}`}>
      {done ? (
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <path d="M3 7.5l2.5 2.5 5.5-5.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ) : (
        <span className="cart-step-num">{idx + 1}</span>
      )}
      <span className="cart-step-label">{label}</span>
    </div>
  );

  return (
    <>
      <div className="cart-page-backdrop" onClick={handleBack} />
      <div className="cart-page" ref={cartPageRef}>
        <div className="cart-page-header">
          <button className="cart-page-back" onClick={handleBack} aria-label="Back to menu">
            ←
          </button>
          <h1>Your Cart</h1>
          <span className="cart-page-count">{itemCount} item{itemCount === 1 ? "" : "s"}</span>
        </div>

        {items.length > 0 && (
          <div className="cart-page-total-bar">
            <div className="cart-total-info">
              <span className="cart-total-label">Total payable</span>
              <span className="cart-total-breakdown">
                Subtotal ₹{Math.round(subtotal)}
                {taxTotal > 0 ? ` · incl. taxes ₹${Math.round(taxTotal)}` : ""}
              </span>
            </div>
            <strong className="cart-total-amount">₹{Math.round(grandTotal)}</strong>
          </div>
        )}

        <div className="cart-page-items">
          {items.length === 0 ? (
            <div className="cart-empty">
              <div className="cart-empty-icon">☕</div>
              <p>Your cart is empty</p>
              <button className="cart-empty-btn" onClick={handleBack}>
                Browse Menu
              </button>
            </div>
          ) : (
            items.map((item) => (
              <div className="cart-row" key={item.id}>
                {item.imageUrl && (
                  <img className="cart-row-img" src={item.imageUrl} alt={item.name} />
                )}
                <div className="cart-row-main">
                  <div className="cart-row-top">
                    <div className="cart-row-info">
                      <p className="cart-row-name">
                        {renderDietaryTags(item)}
                        <span className="cart-row-name-text">{item.name}</span>
                      </p>
                      {(item.variantTitle || item.selectedOptions?.length > 0) && (
                        <p className="cart-row-options">
                          {[
                            item.variantTitle,
                            ...(item.selectedOptions || []).map((o) =>
                              Number(o?.price) > 0 ? `${o.title} (+₹${Math.round(o.price)})` : o.title
                            ),
                          ].filter(Boolean).join(", ")}
                        </p>
                      )}
                      {onEditItem && (item.variantTitle || item.selectedOptions?.length > 0) && (
                        <button
                          className="cart-row-edit"
                          onClick={() => { onBack(); onEditItem(item); }}
                        >
                          <EditIcon /> Customise
                        </button>
                      )}
                    </div>
                    <button
                      className="cart-row-delete"
                      onClick={() => removeItem(item.id)}
                      aria-label={`Remove ${item.name} from cart`}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                        <path d="M4 7h16M9 7V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2m2 0v12a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V7h14ZM10 11v6M14 11v6" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </button>
                  </div>
                  <div className="cart-row-bottom">
                    <div className="cart-qty-controls">
                      <button
                        className="cart-qty-btn"
                        onClick={() => updateQuantity(item.id, -1)}
                        aria-label={
                          item.quantity <= 1 ? `Remove ${item.name}` : `Remove one ${item.name}`
                        }
                      >
                        −
                      </button>
                      <span className="cart-qty-value">{item.quantity}</span>
                      <button
                        className="cart-qty-btn"
                        onClick={() => updateQuantity(item.id, 1)}
                        aria-label={`Add one ${item.name}`}
                      >
                        +
                      </button>
                    </div>
                    <strong className="cart-row-line">₹{Math.round(item.unitPrice * item.quantity)}</strong>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {items.length > 0 && (
          <div className="cart-page-footer">
            <div className="cart-steps-indicator">
              {stepLabel(0, "Table", step > 0)}
              <div className={`cart-step-line ${step > 0 ? "done" : ""}`} />
              {stepLabel(1, "Phone", step > 1)}
              <div className={`cart-step-line ${step > 1 ? "done" : ""}`} />
              {stepLabel(2, "Summary", false)}
            </div>

            <div className="cart-step-content" key={step}>
              {step === 0 && (
                <div className="cart-step-panel">
                  <p className="cart-step-title">Your Table Number</p>
                  <p className="cart-step-hint">for barista to serve your order</p>
                  <div className={`cart-step-input-row ${tableError ? "error" : ""}`}>
                    <span className="cart-step-prefix">#</span>
                    <input
                      ref={tableRef}
                      className="cart-step-input cart-step-input-table"
                      type="tel"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      placeholder="e.g. 12"
                      value={tableNumber}
                      onChange={(e) => {
                        setTableNumber(e.target.value.replace(/[^\d]/g, "").slice(0, 2));
                        if (tableError) setTableError(false);
                      }}
                      onKeyDown={(e) => e.key === "Enter" && handleTableContinue()}
                      maxLength={2}
                    />
                  </div>
                  {tableError && (
                    <p className="cart-step-error">Please enter your table number</p>
                  )}
                  <button className="cart-step-btn-primary" onClick={handleTableContinue}>
                    {returnStep !== null ? "Save" : "Continue"}
                  </button>
                </div>
              )}

              {step === 1 && (
                <div className="cart-step-panel">
                  <button className="cart-step-done-tag" onClick={() => goToEdit(0)} aria-label="Edit table number">
                    <span className="cart-step-done-tag-text">Table #{tableNumber}</span>
                    <span className="cart-step-done-tag-edit"><EditIcon /> Edit</span>
                  </button>

                  <div className="cart-reward-card">
                    <div className="cart-reward-icon">
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                        <path d="M12 2.5l2.6 5.27 5.82.85-4.21 4.1.99 5.8L12 15.9l-5.2 2.72.99-5.8-4.21-4.1 5.82-.85L12 2.5Z" fill="currentColor" />
                      </svg>
                    </div>
                    <div className="cart-reward-text">
                      <p className="cart-reward-title">Get one step closer to Circle Membership</p>
                      <p className="cart-reward-sub">Add your number so this order counts towards unlocking your membership</p>
                    </div>
                  </div>

                  <div className="cart-step-title-row">
                    <p className="cart-step-title">Enter your mobile number</p>
                    <button className="cart-step-skip-link" onClick={handlePhoneSkip}>
                      Skip
                    </button>
                  </div>
                  <p className="cart-step-hint">We'll count this order towards your Circle Membership</p>
                  <div className={`cart-step-input-row ${phoneError ? "error" : ""}`}>
                    <span className="cart-step-prefix">+91</span>
                    <input
                      ref={phoneRef}
                      className="cart-step-input"
                      type="tel"
                      inputMode="numeric"
                      placeholder="10-digit number"
                      value={phoneNumber}
                      onChange={(e) => {
                        setPhoneNumber(e.target.value.replace(/[^\d]/g, "").slice(0, 10));
                        if (phoneError) setPhoneError(false);
                      }}
                      onKeyDown={(e) => e.key === "Enter" && handlePhoneContinue()}
                      maxLength={10}
                    />
                  </div>
                  {phoneError && (
                    <p className="cart-step-error">Please enter a valid 10-digit number</p>
                  )}
                  <button className="cart-step-btn-primary" onClick={handlePhoneContinue}>
                    View Order Summary →
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
}
